<template>
  <div>
    <index-card :loading="cardBlockingLoading" fluid>
      <template slot="header">
        <div class="data-export-header d-flex align-center flex-wrap">
          <v-btn text icon large to="/" exact class="data-export-back">
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="data-export-title-block ml-1 ml-sm-2">
            <div class="d-flex align-center">
              <v-icon class="white--text mr-2 data-export-title-icon">mdi-export</v-icon>
              <span class="headline white--text font-weight-medium mb-0">Exportações</span>
            </div>
            <p class="data-export-subtitle mb-0 mt-1 white--text">
              Histórico de arquivos gerados, status e download
            </p>
          </div>
        </div>
      </template>
      <template slot="subheader">
        <div class="data-export-filters">
          <filter-data
            :with_client="false"
            :with_date_start="false"
            :with_date_end="false"
            with_text_search
            text_search_label="Pesquisar por tipo, status ou texto"
            :loading_block="loading.data_exports"
            @doneFilter="handleDoneFilter"
          />
        </div>
      </template>
      <template slot="data_table">
        <div class="data-export-table-shell">
          <v-progress-linear
            v-if="loading.data_exports && data_exports.length > 0"
            absolute
            top
            height="3"
            indeterminate
            color="primary"
            class="data-export-progress"
          />
          <v-data-table
            class="data-export-table elevation-0"
            :headers="headers"
            :search="search"
            :items="data_exports"
            :footer-props="{ itemsPerPageOptions: [10, 30, 50, 100] }"
            :options.sync="pagination"
            :server-items-length="total_ammount"
            mobile-breakpoint="1"
          >
            <template v-slot:item.created_at="{ item }">
              {{ $moment(item.created_at).format('DD/MM/YYYY HH:mm') }}
            </template>

            <template v-slot:item.concluded_at="{ item }">
              {{ item.concluded_at ? $moment(item.concluded_at).format('DD/MM/YYYY HH:mm') : '—' }}
            </template>

            <template v-slot:item.params="{ item }">
              {{ formatDate(item.params) }}
            </template>

            <template v-slot:item.format="{ item }">
              {{ item.format ? String(item.format).toUpperCase() : '—' }}
            </template>

            <template v-slot:item.action="{ item }">
              <span class="data-export-action-cell">{{ exportTextFormat(item) }}</span>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                v-if="statuses_map[item.status]"
                dark
                :color="statuses_map[item.status].color"
                small
                class="data-export-status-chip"
              >
                <v-avatar left>
                  <v-icon x-small>{{ statuses_map[item.status].icon }}</v-icon>
                </v-avatar>
                {{ statuses_map[item.status].text }}
              </v-chip>
              <span v-else class="grey--text">{{ item.status || '—' }}</span>
            </template>

            <template v-slot:item.exported_file_url="{ item }">
              <div v-if="item.exported_file_url">
                <v-btn :href="item.exported_file_url" color="success" target="_blank" download small depressed>
                  <v-icon left small>mdi-download</v-icon>
                  Baixar
                </v-btn>
              </div>
              <span v-else class="grey--text text--darken-1 caption">
                {{ $moment().diff($moment(item.created_at), 'days') >= 3 ? 'Link expirado' : 'URL não gerada' }}
              </span>
            </template>

            <template v-slot:no-data>
              <v-alert :value="true" type="info" outlined class="ma-4" icon="mdi-information-outline">
                Não há exportações para exibir.
              </v-alert>
            </template>

            <template v-slot:no-results>
              <v-alert :value="true" type="warning" outlined class="ma-4" icon="mdi-magnify-close">
                Não foi possível encontrar resultados para "{{ search }}".
              </v-alert>
            </template>
          </v-data-table>
        </div>
      </template>
    </index-card>
  </div>
</template>

<script>
import Api from '@/api/index';

export default {
  name: 'DataExport',
  components: {
    IndexCard: () => import('@/_backoffice/_registrations/components/shared/index/IndexCard.vue'),
    FilterData: () => import('@/components/shared/FilterData.vue'),
  },
  data() {
    return {
      data_exports: [],
      loading: {
        data_exports: false,
      },
      statuses_map: {
        requested: { text: 'Requerido', color: 'amber', icon: 'mdi-check' },
        in_progress: { text: 'Em andamento', color: 'primary', icon: 'mdi-percent' },
        done: { text: 'Pronto', color: 'success', icon: 'mdi-check' },
        error: { text: 'Erro', color: 'error', icon: 'mdi-alert-circle' },
        canceled: { text: 'Cancelado', color: 'blue-grey', icon: 'mdi-times' },
      },
      models_map: {
        Clients: { text: 'Clientes' },
        Bills: { text: 'Boletos' },
        Checks: { text: 'Cheques relacionados' },
        Deposits: { text: 'Depósitos' },
        Drivers: { text: 'Motoristas 3º e Transportadoras' },
        Exchanges: { text: 'Permutas' },
        Orders: { text: 'Finalizados' },
        Pixes: { text: 'Pix' },
        ReturnedChecks: { text: 'Cheques retornados' },
        VrtTransactions: { text: 'Pedidos a receber' },
        RetailOrder: { text: 'Finalizados' },
        SupplierNotes: { text: 'Notas de Fornecedores' },
        Sellings: { text: 'Faturas' },
        Payments: { text: 'Pagamentos' },
        BonusPayments: { text: 'Pagamentos com bônus' },
        AvailableBonus: { text: 'Bônus disponível' },
        Stocks: { text: 'Estoque' },
        CashboxTransactions: { text: 'Caixa do Galpão' },
        StockConference: { text: 'Conferências de estoque' },
        FinancialConsolidatedReports: { text: 'Dados financeiros consolidados' },
        SellerComissionConfigs: { text: 'Configuração de Comissões' },
      },
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['created_at'],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      search: '',
      find_param: null,
      loaded_filter: false,
      total_ammount: 0,
    };
  },
  computed: {
    headers() {
      let headers = [
        { text: 'Criado em', align: 'center', sortable: true, value: 'created_at' },
        { text: 'Concluído em', align: 'center', sortable: true, value: 'concluded_at' },
        { text: 'Exportação', align: 'left', sortable: true, value: 'action', width: '400px' },
        { text: 'Período', align: 'left', sortable: true, value: 'params' },
        { text: 'Status', align: 'center', sortable: true, value: 'status' },
        { text: 'Formato', align: 'center', sortable: true, value: 'format' },
        { text: 'Link', align: 'center', sortable: false, value: 'exported_file_url' },
      ];
      if (this.is_actual_director && !this.is_viewing_as_role) {
        headers.splice(2, 0, { text: 'Solicitante', align: 'center', sortable: true, value: 'user_name' });
      }
      return headers;
    },
    /** Tela cheia só na primeira carga; paginação/atualização usa barra fina sobre a tabela */
    cardBlockingLoading() {
      return this.loading.data_exports && this.data_exports.length === 0;
    },
  },
  watch: {
    pagination: {
      deep: true,
      handler() {
        if (!this.loaded_filter) {
          this.getData();
        }
      },
    },
  },
  methods: {
    handleDoneFilter(filter = {}) {
      this.search = filter.query ? String(filter.query) : '';
      this.find_param = this.findParamFor(this.search);
      if (this.pagination.page !== 1) {
        this.pagination.page = 1;
        return;
      }
      this.getData();
    },
    findParamFor(text) {
      if (!text) return null;

      const search = text.toLowerCase();
      const match_by_models = Object.keys(this.models_map).find((key) => {
        return this.models_map[key]?.text.toLowerCase().includes(search);
      });
      if (match_by_models) return match_by_models;

      return Object.keys(this.statuses_map).find((key) => {
        return this.statuses_map[key].text?.toLowerCase().includes(search);
      });
    },
    getData() {
      this.getDataExports();
    },
    getDataExports() {
      this.loading.data_exports = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
      let params = {
        page,
        limit: itemsPerPage,
        sort_field: sortBy[0] ? sortBy[0] : null,
        sort_dir: sortBy[0] ? (sortDesc[0] ? 'desc' : 'asc') : null,
      };
      if (!!this.search) {
        params['search'] = this.find_param && this.search ? this.find_param : this.search;
      }

      Api.DataExport.index(params)
        .then((req) => req.data)
        .then((data) => {
          this.data_exports = [...data.data];
          this.total_ammount = data.total;
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading.data_exports = false;
        });
    },
    download(id, format) {
      Api.DataExport.show(id)
        .then((r) => {
          const file = new Blob([r.data], { type: r.headers['content-type'] });
          const fileURL = URL.createObjectURL(file);

          const link = document.createElement('a');
          link.href = fileURL;
          link.setAttribute('download', `arquivo.${format}`);
          link.click();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {});
    },
    downloadDataExport(item) {
      Api.DataExport.download(item.id).then((req) => {
        let filename = req.headers['content-disposition'].split('"')[1];
        if (item.format == 'pdf') {
          this.export(req.data, filename, item.format);
        } else if (item.format == 'xlsx') {
          this.exportWithEncode(req.data, filename);
        } else if (item.format == 'csv') {
          this.export(req.data, filename, item.format);
        }
      });
    },
    export(data, filename, format) {
      const url = window.URL.createObjectURL(new Blob([data], { type: `application/${format}` }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(link.href);
    },
    exportWithEncode(data, filename) {
      const binaryData = window.atob(data);
      const arrayBuffer = new ArrayBuffer(binaryData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.addEventListener('load', () => {
        window.URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      });
    },
    formatDate(params) {
      let text = '-';
      if (!params) return text;
      if (params.start_date && params.end_date)
        text =
          this.$moment(params.start_date).format('DD/MM/YYYY') +
          ' a ' +
          this.$moment(params.end_date).format('DD/MM/YYYY');
      if (params.date_start && params.date_end)
        text =
          this.$moment(params.date_start).format('DD/MM/YYYY') +
          ' a ' +
          this.$moment(params.date_end).format('DD/MM/YYYY');
      if (params.stock_date) {
        text = this.$moment(params.stock_date[0]).format('DD/MM/YYYY');
        if (params.stock_date[1]) text = text + ' a ' + this.$moment(params.date_end).format('DD/MM/YYYY');
      }
      if (params.reference_date) text = this.$moment(params.reference_date).format('DD/MM/YYYY');
      if (params.ref_date) text = this.$moment(params.ref_date).format('MMMM [de] YYYY');
      return text;
    },
    exportTextFormat(item) {
      if (['Payments', 'Sellings', 'SupplierNotes'].includes(item.action)) {
        return 'Renovar ' + (item.solution == 'retail' ? 'Galpão' : 'Atacado');
      } else {
        return this.models_map[item.action]?.text || '';
      }
    },
  },
};
</script>

<style scoped>
.data-export-header {
  width: 100%;
  gap: 8px;
}

.data-export-back {
  flex-shrink: 0;
}

.data-export-title-block {
  min-width: 0;
}

.data-export-title-icon {
  opacity: 0.95;
}

.data-export-subtitle {
  font-size: 0.8125rem;
  opacity: 0.88;
  line-height: 1.35;
  max-width: 520px;
}

.data-export-filters {
  width: 100%;
  flex: 1 1 100%;
  min-width: 0;
}

.data-export-filters >>> .filter-data-card {
  width: 100%;
}

.data-export-table-shell {
  position: relative;
  min-height: 120px;
}

.data-export-progress {
  z-index: 2;
}

.data-export-table >>> thead th {
  font-weight: 600 !important;
  font-size: 0.8125rem !important;
  letter-spacing: 0.02em;
}

.data-export-action-cell {
  font-size: 0.875rem;
  line-height: 1.4;
}

.data-export-status-chip {
  font-weight: 500;
}
</style>
